import { ArrowRightIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { string } from "zod";

import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { showSnackbar } from "@/contexts/SnackbarContext";
import { MealSelectionDrawer } from "@/features/flights/components/flight-purchase/additional-services/MealSelectionDrawer";
import { RouteCard } from "@/features/flights/components/flight-purchase/additional-services/RouteCard";
import { SelectMealButton } from "@/features/flights/components/flight-purchase/additional-services/SelectMealButton";
import {
  getCityNameByAirportCode,
  getRoundTripSegmentDirection,
  hasOnlyOneTypeOfAdditionalServices,
  mapServicesToRoutes,
} from "@/features/flights/components/flight-purchase/additional-services/utils";
import type { MappedServiceData } from "@/features/flights/components/flight-purchase/AdditionalServicesSelection/types";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import type { AdditionalServiceData } from "@/features/flights/types/types";
import { getAvailableServices } from "@/features/flights/utils/additionalServices";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import { SUPPORTED_MEAL_ADDITIONAL_SERVICES_CODES } from "@/utils/constants";

export const Route = createFileRoute(
  "/_authenticated/flights/_flights/_flight-offer/offer/$id/step/2/additional-services/meal-selection",
)({
  validateSearch: (search: Record<string, unknown>) => {
    return { travellerId: string().parse(search.travellerId) };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { travellerId } = Route.useSearch();
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const intl = useIntl();

  const { getTravellerServices, setTravellerServices, selectedFlightPayload } =
    useFlightSelectionPayloadContext();
  const { getTravellerIndex } = useTravellersSelectionContext();
  const { data: singleFlightDetails } = useQuery(
    flightAvailabilityQueryOptions(selectedFlightPayload),
  );
  const { data: travellers } = useQuery(travellersQueryOptions());

  const [selectedServices, setSelectedServices] = useState<
    AdditionalServiceData[]
  >(getTravellerServices(travellerId));
  const [activeService, setActiveService] = useState<MappedServiceData | null>(
    null,
  );
  const traveller = travellers?.find((t) => t.id === travellerId);

  const toggleService = (service: MappedServiceData, optionCode: string) => {
    const alreadySelectedServiceIdx = selectedServices.findIndex(
      (s) => s.service.bookingCode === service.service.bookingCode,
    );

    if (alreadySelectedServiceIdx === -1 && optionCode !== "") {
      setSelectedServices((prev) => [
        ...prev,
        {
          mandatory: service.mandatory,
          service: {
            bookingCode: service.service.bookingCode,
            selectedOptionCode: optionCode,
          },
          categoryCode: service.service.service.categoryCode,
        },
      ]);
    } else {
      if (optionCode === "") {
        setSelectedServices((prev) =>
          prev.filter(
            (s) => s.service.bookingCode !== service.service.bookingCode,
          ),
        );
      } else {
        setSelectedServices((prev) =>
          prev.map((s) =>
            s.service.bookingCode === service.service.bookingCode
              ? {
                  ...s,
                  service: { ...s.service, selectedOptionCode: optionCode },
                }
              : s,
          ),
        );
      }
    }
  };

  const [serviceRouteMap, isSingleTypeOfAdditionalService] = useMemo(() => {
    if (!traveller || !singleFlightDetails) return [undefined, false];

    const services = getAvailableServices({
      traveller,
      travellerIndex: getTravellerIndex(travellerId),
      optionalServices: singleFlightDetails.optionalServices,
      mandatoryServices: singleFlightDetails.mandatoryServices,
    });

    const isSingleTypeOfAdditionalService =
      hasOnlyOneTypeOfAdditionalServices(services);

    const routes = mapServicesToRoutes(
      services,
      SUPPORTED_MEAL_ADDITIONAL_SERVICES_CODES,
    );

    return [routes, isSingleTypeOfAdditionalService];
  }, [getTravellerIndex, singleFlightDetails, traveller, travellerId]);

  if (!serviceRouteMap || !singleFlightDetails)
    return (
      <PageLoader>
        <AnimatedLoader icon="airplane" />
      </PageLoader>
    );

  return (
    <div className="px-main">
      <div className="mb-4 text-xl font-medium text-text-title">
        {intl.formatMessage({
          defaultMessage: "Meal selection per segments",
          description: "Section heading for per-flight-segment meal selection",
        })}
      </div>
      <div className="flex flex-col gap-4">
        {Object.entries(serviceRouteMap).map(([route, services]) => {
          const routeCodes = route.split(" - ");
          const segmentDirection = getRoundTripSegmentDirection(
            singleFlightDetails.flights[0],
            singleFlightDetails.flights[1],
            routeCodes[0],
            routeCodes[1],
          );

          return (
            <RouteCard
              key={route}
              routeCodes={routeCodes}
              services={services}
              segmentDirection={segmentDirection}
              flightDetails={singleFlightDetails}
            >
              {services.map((service) => (
                <SelectMealButton
                  key={service.service.bookingCode}
                  service={{ ...service, flightDirection: segmentDirection }}
                  selectedServices={selectedServices}
                  onClick={setActiveService}
                />
              ))}
            </RouteCard>
          );
        })}
      </div>
      <MealSelectionDrawer
        key={activeService?.service.bookingCode}
        open={activeService !== null}
        onClose={() => setActiveService(null)}
        onConfirm={(service, selectedCode) => {
          if (!service) return;
          toggleService(service, selectedCode);
        }}
        selectedOptionCode={
          selectedServices.find(
            (selectedService) =>
              selectedService.service.bookingCode ===
              activeService?.service.bookingCode,
          )?.service.selectedOptionCode
        }
        service={activeService}
        headerTitle={
          <div className="flex items-center justify-end gap-1 text-sm text-text-tertiary">
            <div>
              {getCityNameByAirportCode(
                activeService?.service.route.departureCode,
                singleFlightDetails?.flights,
              )}
            </div>
            <ArrowRightIcon
              size={12}
              color="#000"
              className="rtl:-scale-x-100"
            />
            <div>
              {getCityNameByAirportCode(
                activeService?.service.route.arrivalCode,
                singleFlightDetails?.flights,
              )}
            </div>
          </div>
        }
      />

      <PersistentDrawer>
        <div className="mt-auto">
          <Button
            fullWidth
            variant="contained"
            size="medium"
            onClick={() => {
              setTravellerServices(travellerId, selectedServices);
              showSnackbar({
                content: intl.formatMessage({
                  defaultMessage: "Meals added to the flight",
                  description:
                    "Confirmation that meals have been added to the flight for a traveller",
                }),
              });
              if (isSingleTypeOfAdditionalService) {
                navigate({
                  to: "/flights/offer/$id/step/2",
                  params: { id },
                });
              } else {
                navigate({
                  to: "/flights/offer/$id/step/2/additional-services",
                  params: { id },
                  search: { travellerId },
                });
              }
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Confirm",
              description:
                "Generic 'Confirm' button label to submit or accept an action",
            })}
          </Button>
        </div>
      </PersistentDrawer>
    </div>
  );
}
